import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * A dropdown action menu (spec §7.2). `trigger` is a render prop so the
 * caller supplies the actual trigger element (a `Button`/`IconButton`/custom
 * node) while Menu wires up the interaction props it needs.
 *
 * Keyboard: roving tabindex across items, ArrowUp/ArrowDown move focus,
 * Home/End jump to the first/last enabled item, Escape closes and returns
 * focus to the trigger. Menu items are intentionally not part of the page Tab
 * order — Tab closes the menu rather than cycling through it, matching the
 * ARIA APG menu-button pattern.
 *
 * @param {object} props
 * @param {(triggerProps: object) => import('react').ReactElement} props.trigger
 * @param {({id: string, label: import('react').ReactNode, icon?: import('react').ReactNode, tone?: 'danger', disabled?: boolean, onSelect?: () => void}|{type: 'separator'})[]} props.items
 * @param {'start'|'end'} [props.align='start']
 * @param {boolean} [props.open] - controlled open state.
 * @param {(open: boolean) => void} [props.onOpenChange]
 * @param {string} [props.className] - applied to the `.etch-menu` panel.
 */
export function Menu({ trigger, items, align = 'start', open: openProp, onOpenChange, className = '', ...rest }) {
  const isControlled = openProp !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? openProp : internalOpen;
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [position, setPosition] = useState(null);
  const menuId = useId();

  const setOpenState = (next) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  const closeAndRefocus = () => {
    setOpenState(false);
    triggerRef.current?.focus?.();
  };

  useEffect(() => {
    if (!open) return undefined;

    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        left: align === 'end' ? undefined : rect.left + window.scrollX,
        right: align === 'end' ? window.innerWidth - rect.right - window.scrollX : undefined
      });
    }

    const firstItem = menuRef.current?.querySelector('[role="menuitem"]:not([aria-disabled="true"])');
    firstItem?.focus();

    const handlePointerDown = (event) => {
      if (menuRef.current?.contains(event.target) || triggerRef.current?.contains(event.target)) return;
      setOpenState(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, align]);

  const handleKeyDown = (event) => {
    const focusable = Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]:not([aria-disabled="true"])') ?? []);
    const currentIndex = focusable.indexOf(document.activeElement);

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        closeAndRefocus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        focusable[(currentIndex + 1) % focusable.length]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusable[(currentIndex - 1 + focusable.length) % focusable.length]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        focusable[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        focusable[focusable.length - 1]?.focus();
        break;
      // The panel is portalled to the end of <body>, so letting the browser
      // continue from the focused menu item would resume tab order past the end
      // of the document. Restoring the trigger first makes the next Tab land on
      // the element after the button, as the APG menu-button pattern requires.
      case 'Tab':
        closeAndRefocus();
        break;
      default:
        break;
    }
  };

  return (
    <>
      {trigger({
        ref: triggerRef,
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': open ? menuId : undefined,
        onClick: () => setOpenState(!open)
      })}
      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            className={`etch-menu ${className}`.trim()}
            style={position ?? undefined}
            onKeyDown={handleKeyDown}
            {...rest}
          >
            {items.map((item, index) =>
              item.type === 'separator' ? (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`separator-${index}`} className="etch-menu__separator" role="separator" />
              ) : (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  className="etch-menu__item"
                  data-tone={item.tone}
                  aria-disabled={item.disabled || undefined}
                  disabled={item.disabled}
                  onClick={() => {
                    if (item.disabled) return;
                    item.onSelect?.();
                    closeAndRefocus();
                  }}
                >
                  {item.icon}
                  {item.label}
                </button>
              )
            )}
          </div>,
          document.body
        )}
    </>
  );
}

export default Menu;
